import React, { useContext, useState } from 'react';
import swal from 'sweetalert2';
import Workflow from '../../../components/Workflow';
import { useAgents } from '../../../contexts/AgentsContext';
import { AppContext } from '../../../contexts/AppContext';
import { RunWorkflowDialog } from "../../../components/Dialogs";
import GenerateWorkflowDialog from '../../../components/Workflows/GenerateWorkflowDialog';
import { Button } from '../../../components/StyledElements';
import { extractGeneratedGraph, loadGeneratedWorkflowGraph } from '../../../services/workflowGeneration';
import { resolveGeneratorClientId } from "../../../utils/workflowGenerationRequest";

export const WorkflowGraph = ({ style, onClose = () => { } }) => {
  const { updatedWorkflow, setUpdatedWorkflow, } = useAgents();
  const { selectedClient } = useContext(AppContext);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  // The editor captures its initial graph at mount, so a generated graph is applied by
  // remounting it rather than by swapping the prop.
  const [graphVersion, setGraphVersion] = useState(0);

  if (!updatedWorkflow) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
      <span>Loading workflow...</span>
    </div>;
  }

  const applyGeneratedWorkflow = async (saved, results) => {
    setShowGenerateDialog(false);

    const inlineGraph = extractGeneratedGraph(results);
    const generatedGraph = inlineGraph ?? (await loadGeneratedWorkflowGraph(saved?.public_id))?.graph ?? null;

    if (!generatedGraph) {
      swal.fire({
        title: "Generated",
        text: "The workflow was generated, but no graph came back, so it could not be loaded here.",
        icon: "info",
      });
      return;
    }

    setUpdatedWorkflow({ ...updatedWorkflow, graph: generatedGraph });
    setGraphVersion((version) => version + 1);

    swal.fire({
      title: "Workflow generated",
      text: `"${saved?.name ?? results?.name ?? "Workflow"}" was loaded into this editor. It replaces the graph below — click Save Changes to keep it on this workflow.`,
      icon: "success",
    });
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: "10px", ...style }}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
        <div style={{ fontSize: "13px", color: "#a0a6b1", flex: 1 }}>
          Describe the workflow in plain language and let the generator build the graph.
        </div>
        <Button onClick={() => setShowGenerateDialog(true)}>Generate from description</Button>
      </div>

      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <Workflow
          key={graphVersion}
          workflow={updatedWorkflow.graph}
          onWorkflowChange={(graph) => {
            const newWorkflow = { ...updatedWorkflow };
            newWorkflow.graph = graph;
            setUpdatedWorkflow(newWorkflow);
          }}
          onRunWorkflow={() => {
            setShowRunDialog(true);
          }}
        />
      </div>

      <GenerateWorkflowDialog
        show={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onGenerated={applyGeneratedWorkflow}
        defaultName={updatedWorkflow?.name ?? ""}
        defaultDescription={updatedWorkflow?.description ?? ""}
        defaultPurpose={updatedWorkflow?.purpose ?? ""}
        defaultKind="agent"
        clientId={resolveGeneratorClientId(selectedClient, updatedWorkflow)}
      />

      {showRunDialog &&
        <RunWorkflowDialog workflowGraph={updatedWorkflow.graph} workflow={updatedWorkflow} show={true} onClose={() => setShowRunDialog(false)} />
      }
    </div>
  );
};

export default WorkflowGraph;
